import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import { fmtDateTime } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";
import { DropdownMenu, DropdownMenuContent, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Bell, MessageSquareText, AlertTriangle, Info, ThumbsUp, CheckCheck, Trash2 } from "@/components/icons";
import HelpTip from "@/components/HelpTip";
import { getSomSinoAtivo } from "@/lib/webpush";

const iconFor = (tipo) =>
  tipo === "curtida" ? ThumbsUp : tipo === "orientacao" ? MessageSquareText : tipo === "error" || tipo === "warning" ? AlertTriangle : Info;

// NOTIF-1 — toca um "ding" curto via WebAudio (sem baixar arquivo).
// Mantém volume discreto; silencioso quando o usuário desligou em Ajustes.
function tocarDing() {
  if (!getSomSinoAtivo()) return;
  try {
    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (!Ctx) return;
    const ctx = new Ctx();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = "sine";
    osc.frequency.setValueAtTime(880, ctx.currentTime);
    osc.frequency.exponentialRampToValueAtTime(1320, ctx.currentTime + 0.12);
    gain.gain.setValueAtTime(0.0001, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.18, ctx.currentTime + 0.02);
    gain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.35);
    osc.connect(gain).connect(ctx.destination);
    osc.start();
    osc.stop(ctx.currentTime + 0.4);
    setTimeout(() => { try { ctx.close(); } catch (e) { void e; } }, 500);
  } catch (e) { void e; /* silencioso */ }
}

export default function NotificationBell() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  // Seq para descartar respostas de fetches iniciados por um usuário anterior
  // caso a troca de sessão aconteça enquanto uma request está no ar.
  const uidRef = useRef(null);
  // NOTIF-1 — rastreia IDs vistos para detectar NOVOS alertas e tocar o som.
  const seenRef = useRef(new Set());
  const firstLoadRef = useRef(true);

  const load = useCallback(async (forUid) => {
    try {
      const { data } = await api.get("/alertas");
      // Se o usuário mudou enquanto essa resposta estava em voo, descarta.
      if (forUid !== uidRef.current) return;
      const arr = data || [];
      // Detecta notificações NOVAS (não vistas ainda) e, se houver novas
      // não lidas, dispara o ding uma vez. No primeiro load não toca.
      if (!firstLoadRef.current) {
        const temNova = arr.some((a) => !a.lida && !seenRef.current.has(a.id));
        if (temNova) tocarDing();
      }
      seenRef.current = new Set(arr.map((a) => a.id));
      firstLoadRef.current = false;
      setItems(arr);
    } catch { /* silencioso */ }
  }, []);

  useEffect(() => {
    // Ao (re)montar OU trocar de usuário sem full reload (login por cima),
    // zera o cache em memória IMEDIATAMENTE e refaz o fetch para o novo user.
    // Sem isso, o array do usuário anterior fica visível por até 30s.
    uidRef.current = user?.id || null;
    setItems([]);
    seenRef.current = new Set();
    firstLoadRef.current = true;
    if (!user?.id) return undefined;
    load(user.id);
    const t = setInterval(() => load(user.id), 30000);
    return () => clearInterval(t);
  }, [user?.id, load]);

  const unread = items.filter((a) => !a.lida).length;
  const lidas = items.filter((a) => a.lida).length;

  const openItem = async (a) => {
    setOpen(false);
    if (!a.lida) {
      try { await api.patch(`/alertas/${a.id}/read`); } catch { /* ignore */ }
      setItems((prev) => prev.map((x) => (x.id === a.id ? { ...x, lida: true } : x)));
    }
    // `link` (novo) tem prioridade — usado pelas cobranças por WhatsApp
    // para levar direto à ficha com o destaque `?cobranca={id}`.
    if (a.link) navigate(a.link);
    else if (a.cliente_id) navigate(`/cliente/${a.cliente_id}`);
    else if (a.deal_id) navigate(`/deal/${a.deal_id}`);
  };

  const marcarTodas = async (e) => {
    e.stopPropagation();
    if (busy) return;
    setBusy(true);
    try {
      await api.post("/alertas/read-all");
      setItems((prev) => prev.map((x) => ({ ...x, lida: true })));
      toast.success("Notificações marcadas como lidas");
    } catch { toast.error("Não foi possível marcar"); }
    finally { setBusy(false); }
  };

  const limparLidas = async (e) => {
    e.stopPropagation();
    if (busy) return;
    setBusy(true);
    try {
      await api.delete("/alertas/lidas");
      setItems((prev) => prev.filter((x) => !x.lida));
      toast.success("Notificações lidas removidas");
    } catch { toast.error("Não foi possível limpar"); }
    finally { setBusy(false); }
  };

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <HelpTip label="Notificações">
      <DropdownMenuTrigger asChild>
        <button data-testid="notif-bell" aria-label="Notificações" className="relative p-2 rounded-lg hover:bg-slate-100">
          <Bell className="w-5 h-5 text-navy" />
          {unread > 0 && (
            <span data-testid="notif-badge" className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-bad text-white text-[10px] font-bold flex items-center justify-center">
              {unread > 9 ? "9+" : unread}
            </span>
          )}
        </button>
      </DropdownMenuTrigger>
      </HelpTip>
      <DropdownMenuContent align="end" className="w-[360px] p-0 max-h-[70vh] overflow-y-auto">
        <div className="px-4 py-3 border-b border-black/5 flex items-center justify-between gap-2">
          <div className="flex items-baseline gap-2 min-w-0">
            <span className="font-semibold text-navy text-sm">Notificações</span>
            {unread > 0 && <span className="text-xs text-muted-foreground">{unread === 1 ? "1 não lida" : `${unread} não lidas`}</span>}
          </div>
          <div className="flex items-center gap-1 shrink-0">
            {unread > 0 && (
              <button type="button" onClick={marcarTodas} disabled={busy} data-testid="notif-mark-all"
                className="text-[11px] font-medium text-brand-hover hover:bg-brand-soft rounded px-2 py-1 flex items-center gap-1 disabled:opacity-50">
                <CheckCheck className="w-3.5 h-3.5" /> Marcar todas
              </button>
            )}
            {lidas > 0 && (
              <button type="button" onClick={limparLidas} disabled={busy} data-testid="notif-clear-read"
                className="text-[11px] font-medium text-slate-500 hover:bg-slate-100 rounded px-2 py-1 flex items-center gap-1 disabled:opacity-50" title="Remove as lidas do sino">
                <Trash2 className="w-3.5 h-3.5" /> Limpar
              </button>
            )}
          </div>
        </div>
        {items.length === 0 ? (
          <div className="px-4 py-8 text-center text-sm text-muted-foreground">Nenhuma notificação.</div>
        ) : (
          items.slice(0, 30).map((a) => {
            const Icon = iconFor(a.tipo);
            return (
              <button key={a.id} onClick={() => openItem(a)} data-testid={`notif-item-${a.id}`}
                className={`w-full text-left px-4 py-3 border-b border-black/5 last:border-0 hover:bg-slate-50 flex gap-3 ${a.lida ? "" : "bg-brand-soft/30"}`}>
                <Icon className={`w-4 h-4 mt-0.5 shrink-0 ${a.tipo === "orientacao" || a.tipo === "curtida" ? "text-brand" : a.tipo === "error" ? "text-bad" : a.tipo === "warning" ? "text-warn" : "text-slate-400"}`} />
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-medium text-navy leading-snug">{a.titulo}</div>
                  <div className="text-xs text-muted-foreground line-clamp-2 mt-0.5">{a.mensagem}</div>
                  <div className="text-[10px] text-slate-400 mt-1">{fmtDateTime(a.created_at)}</div>
                </div>
                {!a.lida && <span className="w-2 h-2 rounded-full bg-brand mt-1.5 shrink-0" />}
              </button>
            );
          })
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
